import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatInZone } from '../lib/timezone';
import useDisplayZone from '../hooks/useDisplayZone';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

import { useContacts } from '../hooks/useContacts';
import { useQueryClient } from '@tanstack/react-query';
import {
    useInboxEmails, useCommunications, useGmailStatus,
    useLogCommunication,
} from '../hooks/useInbox';
import { useOutlookStatus } from '../hooks/useOutlook';
import { useTrackingSSE } from '../hooks/useTrackingSSE';
import {
    Search, Mail, Phone, MessageSquare, Bell,
    ChevronLeft,
    Archive, Reply, Eye, MousePointer,
    Send, UserPlus, ExternalLink,
    RefreshCw,
    Maximize2, Minimize2, Mic, MicOff, MessageCircle
} from 'lucide-react';
import WhatsAppChat from '../components/WhatsAppChat';
import InboxTasksTab from '../components/inbox/InboxTasksTab';

import MailboxSwitcher from '../components/inbox/MailboxSwitcher';
import ChannelStrip, { CHANNELS } from '../components/inbox/ChannelStrip';
import InboxStatsInline from '../components/inbox/InboxStatsInline';
import { filtersFromSearch } from '../components/inbox/InboxFiltersBar';
import UnifiedInboxSurface from '../components/inbox/UnifiedInboxSurface';
import { inboxFolderLink, tabSyncParams } from '../components/inbox/inboxDeepLink';
import PersonalInboxSurface from '../components/inbox/PersonalInboxSurface';
import { InboxSyncChrome } from '../components/inbox/InitialSyncProgress';
import { SyncProgressProvider } from '../hooks/useSyncProgress';
import InboxSettingsPage from './InboxSettingsPage';
import { usePermissions } from '../context/PermissionsContext';
import { MY_INBOX_ID, LAST_MAILBOX_KEY } from '../components/inbox/inboxRules';
import { getComposeRoute } from '../components/inbox/composeRouting';
import ProviderConnectionBadges from '../components/inbox/ProviderConnectionBadges';
import { useInboxAccess, useInboxUnrepliedCounts, useLastMailbox, useInboxMailboxes, useInboxStats, mailboxKeys } from '../hooks/useUnifiedInbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Voice-to-Text Button ────────────────────────────────────────────────── */
function VoiceButton({ onResult, testId, isTextarea }) {
    const [listening, setListening] = useState(false);

    const toggle = () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) { toast.error('Voice input not supported in this browser'); return; }

        if (listening) { setListening(false); return; }

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
            const text = event.results[0][0].transcript;
            onResult(text);
            setListening(false);
        };
        recognition.onerror = () => { setListening(false); toast.error('Voice recognition failed'); };
        recognition.onend = () => setListening(false);

        recognition.start();
        setListening(true);
    };

    return (
        <button
            type="button"
            onClick={toggle}
            data-testid={testId}
            title={listening ? 'Stop listening' : 'Voice input'}
            className={`absolute right-2 ${isTextarea ? 'top-2' : 'top-1/2 -translate-y-1/2'} p-1.5 rounded-lg transition-colors ${
                listening
                    ? 'bg-red-100 text-red-600 animate-pulse'
                    : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100'
            }`}
        >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
    );
}

// Tab configuration
/* The channel list now lives in `ChannelStrip` — five items, as the design
   draws them. `Primary` was folded into `Emails` (one list, two names), `Tasks`
   moved to its own page, and `Tracking` and `Reminders` were removed from the
   product by decision. See ChannelStrip's header. */
const INBOX_TABS = CHANNELS;

export default function Inbox() {
    const { user, getAuthHeader } = useAuth();
    const displayZone = useDisplayZone();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlParams = new URLSearchParams(window.location.search);
    // `emails` is what `primary` was called: the merged mail list. An old
    // bookmark carrying ?tab=primary still lands on it rather than on nothing.
    const rawTab = urlParams.get('tab');
    const initialTab = !rawTab || rawTab === 'primary' ? 'emails' : rawTab;

    // ── Unified inbox context selection ──────────────────────────────────
    // The personal page owns both contexts now. Selecting a unified inbox
    // renders it in place instead of navigating to a separate page.
    const unifiedInboxAccess = useInboxAccess();
    const { lastMailboxId, setLastMailboxId } = useLastMailbox(LAST_MAILBOX_KEY);

    // Issue #662: the permission store (#663) is the single source of truth
    // for "may this caller manage THIS inbox". Resolved once here and passed
    // down — MailboxFilterSelect and the switcher footer stay permission-free
    // leaves, and #632's panel actions can reuse the same predicate.
    const { can, permissionsUnknown, refetch: refetchPermissions } = usePermissions();
    const canManageInbox = useCallback(
        inboxIdToCheck =>
            !permissionsUnknown &&
            Boolean(inboxIdToCheck) &&
            can('inbox:manage', { type: 'inbox', id: inboxIdToCheck }),
        [can, permissionsUnknown]
    );
    const [manageInboxId, setManageInboxId] = useState(null);
    const manageSettingsModal = manageInboxId ? (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4"
            role="dialog"
            aria-modal="true"
            data-testid="inbox-settings-modal"
            onClick={event => {
                if (event.target === event.currentTarget) setManageInboxId(null);
            }}
        >
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <InboxSettingsPage
                    inboxId={manageInboxId}
                    onClose={() => {
                        setManageInboxId(null);
                        refetchPermissions();
                    }}
                />
            </div>
        </div>
    ) : null;
    const { data: unrepliedByInbox = {} } = useInboxUnrepliedCounts(
        unifiedInboxAccess.inboxes
    );
    // Live updates: SSE invalidates the query caches when a new message lands.
    // The single useTrackingSSE() call (below, near line 485) handles both
    // tracking events AND inbox_message events — one connection, all events.
    // Polling (60s) remains as the fallback.

    const requestedInboxId = searchParams.get('inbox');
    const selectedInboxId = useMemo(() => {
        if (unifiedInboxAccess.isLoading) return MY_INBOX_ID;
        const ids = new Set((unifiedInboxAccess.inboxes || []).map(i => i.inbox_id));
        if (requestedInboxId && ids.has(requestedInboxId)) return requestedInboxId;
        if (!requestedInboxId && lastMailboxId && ids.has(lastMailboxId)) return lastMailboxId;
        return MY_INBOX_ID;
    }, [requestedInboxId, lastMailboxId, unifiedInboxAccess.inboxes, unifiedInboxAccess.isLoading]);

    const selectedUnifiedInbox = useMemo(
        () => (unifiedInboxAccess.inboxes || []).find(i => i.inbox_id === selectedInboxId) || null,
        [unifiedInboxAccess.inboxes, selectedInboxId]
    );

    // ── Mailbox connection selection (Phase 8) ───────────────────────────
    // The `mailbox` query param carries the explicit mailbox connection id
    // from compose routing. This makes provider selection deterministic:
    // the reply composer picks the provider based on this explicit selection,
    // not array order or stale cache.
    const requestedMailboxConnectionId = searchParams.get('mailbox');

    // Fetch mailbox connections for the selected unified inbox so the
    // MailboxSwitcher can show provider badges and the reply composer can
    // resolve the selected connection.
    const { data: selectedInboxMailboxesData } = useInboxMailboxes(
        selectedInboxId !== MY_INBOX_ID ? selectedInboxId : null
    );
    // Build mailboxesByInbox for the MailboxSwitcher — only the selected
    // inbox's mailboxes are fetched; others fall back to the dot icon.
    //
    // The `?? []` fallback lives INSIDE the memo. Outside it, the expression
    // produced a fresh array identity on every render whenever the query had
    // no data, so the memo's dependency changed every render and it recomputed
    // unconditionally — the memo did nothing except cost a comparison.
    const mailboxesByInbox = useMemo(() => {
        const mailboxes = selectedInboxMailboxesData?.data || [];
        if (selectedInboxId === MY_INBOX_ID || mailboxes.length === 0) return {};
        return { [selectedInboxId]: mailboxes };
    }, [selectedInboxId, selectedInboxMailboxesData]);

    const handleMailboxSelect = useCallback((inboxId) => {
        if (inboxId === MY_INBOX_ID) {
            setLastMailboxId(null);
            navigate('/inbox');
            return;
        }
        setLastMailboxId(inboxId);
        // A fresh URL on purpose: the previous inbox's `inbox_folder` (and its
        // filters) belong to that inbox, and must not follow the user to this one.
        navigate(inboxFolderLink(inboxId));
    }, [navigate, setLastMailboxId]);

    // ── Personal inbox data ───────────────────────────────────────────────
    // Personal hooks are gated on `isPersonalContext` to avoid background
    // polling when a unified inbox is selected. The hooks still return
    // cached data (React Query keeps it in the cache) but stop fetching.
    const isPersonalContext = selectedInboxId === MY_INBOX_ID;

    // ── First-sync gate ──────────────────────────────────────────────────
    //
    // A newly connected mailbox used to drop the user straight into an empty
    // inbox that filled a page at a time with nothing on screen to say so.
    // The walk now runs server-side against a job row, and this reads it.
    //
    // Gating HERE, on the page, is what makes "let's say they force navigate
    // by typing the URL directly" work: the dialog is a function of job state,
    // not of where the user came from, so a pasted URL, a refresh and a second
    // tab all land on the same thing.
    //
    // Per lane, because they are different mailboxes with different jobs.
    const syncLaneId = isPersonalContext ? null : selectedInboxId;

    // ─── React Query: cached data (survives navigation) ─────────────────
    const { data: contactsData, isLoading: loadingContacts } = useContacts();
    const { data: inboxData, isLoading: loadingInbox, error: inboxError } = useInboxEmails(isPersonalContext);
    const { data: communications = [], error: communicationsError } = useCommunications(isPersonalContext);
    const { data: gmailStatus, error: gmailStatusError } = useGmailStatus();
    const { data: outlookStatus } = useOutlookStatus();

    // Derived values from cached data
    const contacts = contactsData || [];
    const emails = inboxData?.emails || [];
    
    // Debug: Log errors to console
    useEffect(() => {
        if (inboxError) console.error('Inbox emails error:', inboxError);
        if (communicationsError) console.error('Communications error:', communicationsError);
    }, [inboxError, communicationsError]);

    // ─── Email Tracking ──────────────────────────────────────────────────

    // `primary` is the server's name for what the strip calls `emails`.
    const rawCounts = inboxData?.counts || {};
    const tabCounts = {
        ...rawCounts,
        emails: rawCounts.emails ?? rawCounts.primary ?? 0,
    };
    // useInboxEmails is kept for channel counts (the `counts` field), which
    // usePersonalInboxEmails does not provide. The old useGmailEmails /
    // useOutlookEmails hooks were removed: PersonalInboxSurface fetches every
    // email source through usePersonalInboxEmails.

    // Show UI as soon as we have any cached data or first query resolves
    const loading = (loadingInbox || loadingContacts) && emails.length === 0 && contacts.length === 0;
    useTrackingSSE();  // Real-time notifications

    // ─── Mutations ──────────────────────────────────────────────────────
    const logCommMutation = useLogCommunication();

    // ─── UI-only state (ephemeral, OK to lose on navigation) ────────────
    const [activeTab, setActiveTab] = useState(initialTab);

    // The title row's metrics. Same hook and same key the unified surface uses,
    // so react-query serves ONE request to both and the header cannot report a
    // different number from the list beneath it.
    const headerMailbox = filtersFromSearch(location.search).mailbox;
    const { data: headerStats } = useInboxStats(
        selectedInboxId !== MY_INBOX_ID ? selectedInboxId : null,
        { mailbox: headerMailbox },
    );
    const [showLogModal, setShowLogModal] = useState(false);
    const [logModalExpanded, setLogModalExpanded] = useState(false);
    const [logForm, setLogForm] = useState({
        type: 'call', contact_id: '', account_id: '', subject: '', notes: '', duration: ''
    });

    // Sync activeTab to URL search params. Every other param — `inbox`, the
    // unified surface's `inbox_folder`, its filters — is carried through.
    useEffect(() => {
        const next = tabSyncParams(searchParams, activeTab);
        if (next) setSearchParams(next, { replace: true });
    }, [activeTab, searchParams, setSearchParams]);

    // Resolve signature merge variables with current user data
    // ─── Action handlers (using mutations) ──────────────────────────────

    // Log communication
    const handleLogCommunication = async () => {
        if (!logForm.type || !logForm.subject) {
            toast.error('Please fill required fields');
            return;
        }
        try {
            await logCommMutation.mutateAsync(logForm);
            toast.success('Communication logged');
            setShowLogModal(false);
            setLogForm({ type: 'call', contact_id: '', account_id: '', subject: '', notes: '', duration: '' });
        } catch (err) {
            toast.error('Failed to log communication');
        }
    };

    // View email detail — navigate to full-page route instead of opening modal
    const viewEmailDetail = (email) => {
        const commId = email.comm_id || email.email_id || email.tracking_id || 'unknown';
        // Enrich tracked emails with fields PersonalThreadView expects
        const enrichedEmail = email._source === 'tracking' ? {
            ...email,
            contact_email: email.to_email || email.contact_email || '',
            contact_name: email.to_email || 'Tracked email',
            from: email.to_email || '',
            subject: email.subject || '(No subject)',
            body: email.body || '<p style="color:#a8a29e">No content available for tracked emails. Open the tracking details to see open/click statistics.</p>',
            sent_at: email.sent_at || email.date,
            to: email.to_email || '',
        } : email;
        navigate(`/inbox/email/${commId}`, { state: { email: enrichedEmail } });
    };
    
    // Check for critical errors
    const hasError = isPersonalContext && inboxError;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-stone-50">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-stone-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500 text-sm">Loading inbox...</p>
                </div>
            </div>
        );
    }
    
    if (hasError) {
        return (
            <div className="flex items-center justify-center h-screen bg-stone-50">
                <div className="text-center max-w-md p-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Mail className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-stone-900 mb-2">Unable to load inbox</h2>
                    <p className="text-sm text-stone-500 mb-4">
                        {inboxError?.message || 'There was a problem loading your emails. Please try refreshing the page.'}
                    </p>
                    <Button onClick={() => window.location.reload()} className="bg-stone-900 hover:bg-stone-800">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Page
                    </Button>
                </div>
            </div>
        );
    }

    return (
        /* h-full, not min-h-screen. `min-h-screen` sets a floor with no
           ceiling, so the column grows to fit its content and the inner
           `overflow-hidden` row is handed that grown height — the message list
           then renders at full content height and its own scroll container
           never scrolls. The inbox is a fixed-height app surface: it fills the
           shell and scrolls internally. */
        /* `relative` so the first-sync dialog's `absolute inset-0` covers
           THIS surface rather than the viewport — the shell's rail and the
           workspace nav stay usable while a mailbox imports. Blocking the
           inbox is the requirement; blocking the product is not. */
        /* ONE provider per lane, wrapping everything that wants progress.
           `InboxFiltersBar` consumes it for the per-mailbox sliders; before
           this it called `useSyncProgress` itself, and a second React Query
           observer on the same key gets its own interval — two out-of-phase
           timers polling one lane. */
        <SyncProgressProvider inboxId={syncLaneId}>
        <div className="relative h-full min-h-0 bg-white flex flex-col" data-testid="inbox-page">
            <InboxSyncChrome />
            {/* The page-level "Search..." box that used to sit here is gone.
                Its state was set and never read — typing in the first control
                on the page did nothing, while a second, working search sat in
                the filter bar below it. Search belongs on the surface that
                owns the query: PersonalInboxToolbar for personal mail,
                InboxFiltersBar for a unified inbox. Both are server-side. */}

            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* Main Content */}
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {/* Inbox Header */}
                    <div className="px-4 py-3 border-b border-stone-100">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                {/* Design A (Amendment 2): the page title IS the mailbox
                                    switcher, and it renders here as well as on the unified
                                    inbox page. Switching between your own mail and a
                                    department's has to be one menu, not two destinations —
                                    otherwise a departmental inbox reads as a separate product.
                                    Falls back to a plain heading when the tenant has no
                                    unified inbox, which is the majority case today. */}
                                {unifiedInboxAccess.hasAccess ? (
                                    <MailboxSwitcher
                                        inboxes={unifiedInboxAccess.inboxes}
                                        selectedId={selectedInboxId}
                                        onSelect={handleMailboxSelect}
                                        user={user}
                                        unreadByInbox={unrepliedByInbox}
                                        isAdmin={unifiedInboxAccess.isAdmin}
                                        onManageInboxes={() => {
                                            // The switcher action is organisation-level
                                            // management. Scoped managers use the
                                            // unified inbox toolbar's Manage Mailbox
                                            // action for the inbox they are granted.
                                            navigate('/admin/unified-inbox');
                                        }}
                                    />
                                ) : (
                                    <h1 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Inboxes
                                    </h1>
                                )}

                            </div>

                            {/* §10 puts the channel strip beside the switcher on a
                                personal inbox, and the response metrics there on a
                                unified one. Compose and Log moved OUT of this row:
                                Compose is the control strip's violet primary, and
                                Log is the fifth channel. */}
                            {selectedInboxId === MY_INBOX_ID ? (
                                <ChannelStrip
                                    active={activeTab}
                                    onSelect={setActiveTab}
                                    counts={tabCounts}
                                    onLog={() => setShowLogModal(true)}
                                />
                            ) : (
                                <InboxStatsInline stats={headerStats} />
                            )}
                        </div>
                        </div>

                        {/* Unified inbox surface — renders in place when a unified
                            inbox is selected via the switcher or ?inbox= param. */}
                        {selectedInboxId !== MY_INBOX_ID && selectedUnifiedInbox ? (
                            <>
                            <UnifiedInboxSurface
                                selectedInbox={selectedUnifiedInbox}
                                user={user}
                                getAuthHeader={getAuthHeader}
                                isAdmin={unifiedInboxAccess.isAdmin}
                                canManage={canManageInbox(selectedInboxId)}
                                onManageMailboxes={setManageInboxId}
                            />
                            {manageSettingsModal}
                            </>
                        ) : (
                        <>
                        {/* The channel tabs moved into the title row as
                            `ChannelStrip` (§10). The provider badges that used to
                            sit at the end of this row are now the control strip's
                            source chips, where they double as a filter. */}

                    {/* The Tracking channel and its open/click table were removed
                        from the inbox by decision — the design's strip is five
                        channels, and tracking was not one of them. The `/tracking`
                        data itself is untouched; nothing reads it here any more. */}
                    {activeTab === 'whatsapp' ? (
                        <div className="flex-1 overflow-hidden">
                            <WhatsAppChat />
                        </div>
                    ) : activeTab === 'tasks' ? (
                        <InboxTasksTab />
                    ) : (
                        /* emails · calls · messages all read the same personal
                           list; the channel narrows it server-side. */
                        <PersonalInboxSurface user={user} />
                    )}
                </>
                )}
                </div>
            </div>

            {/* Log Communication Modal */}
            <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
                <DialogContent className={`transition-all duration-300 ${logModalExpanded ? 'sm:max-w-3xl' : 'sm:max-w-md'}`}>
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <DialogTitle>Log Communication</DialogTitle>
                                <DialogDescription>Record a call, message, or task</DialogDescription>
                            </div>
                            <button
                                onClick={() => setLogModalExpanded(prev => !prev)}
                                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                                data-testid="expand-log-modal-btn"
                                title={logModalExpanded ? 'Collapse' : 'Expand'}
                            >
                                {logModalExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                            </button>
                        </div>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className={logModalExpanded ? 'grid grid-cols-2 gap-4' : ''}>
                            <div>
                                <Label>Type</Label>
                                <Select value={logForm.type} onValueChange={v => setLogForm(p => ({ ...p, type: v }))}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="call">Call</SelectItem>
                                        <SelectItem value="message">Message</SelectItem>
                                        <SelectItem value="task">Task</SelectItem>
                                        <SelectItem value="reminder">Reminder</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Contact (Optional)</Label>
                                <Select value={logForm.contact_id || "none"} onValueChange={v => setLogForm(p => ({ ...p, contact_id: v === "none" ? "" : v }))}>
                                    <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Select contact" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No contact</SelectItem>
                                        {contacts.map(c => (
                                            <SelectItem key={c.contact_id} value={c.contact_id}>
                                                {c.first_name} {c.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Subject</Label>
                            <div className="relative mt-1">
                                <Input
                                    value={logForm.subject}
                                    onChange={e => setLogForm(p => ({ ...p, subject: e.target.value }))}
                                    placeholder="Brief description"
                                    className="pr-10"
                                    data-testid="log-subject-input"
                                />
                                <VoiceButton
                                    onResult={(text) => setLogForm(p => ({ ...p, subject: (p.subject ? p.subject + ' ' : '') + text }))}
                                    testId="voice-subject-btn"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <div className="relative mt-1">
                                <Textarea
                                    value={logForm.notes}
                                    onChange={e => setLogForm(p => ({ ...p, notes: e.target.value }))}
                                    placeholder="Additional details..."
                                    rows={logModalExpanded ? 8 : 3}
                                    className="pr-10"
                                    data-testid="log-notes-input"
                                />
                                <VoiceButton
                                    onResult={(text) => setLogForm(p => ({ ...p, notes: (p.notes ? p.notes + ' ' : '') + text }))}
                                    testId="voice-notes-btn"
                                    isTextarea
                                />
                            </div>
                        </div>
                        {logForm.type === 'call' && (
                            <div>
                                <Label>Duration (minutes)</Label>
                                <Input
                                    type="number"
                                    value={logForm.duration}
                                    onChange={e => setLogForm(p => ({ ...p, duration: e.target.value }))}
                                    placeholder="15"
                                    className="mt-1"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowLogModal(false)}>Cancel</Button>
                        <Button onClick={handleLogCommunication} className="bg-stone-900 hover:bg-stone-800">
                            Log Communication
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </SyncProgressProvider>
    );
}
